import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CompanyList } from '../util-cmps/database';
import { useDispatch, useSelector } from 'react-redux'
import { RootState, StoreDispatch } from '../../store/configure-store';
import { update } from '../../store/company';
import {utilService} from '../../services/util-service'
import { getCompanySearchMatches } from '../../services/company-search';
import { notifyDropdownOpen } from '../../hooks/use-cached-fetch';


interface CompaniesDropDownProps {
    companies: CompanyList[]
}

export const CompaniesDropDown: React.FC<CompaniesDropDownProps> = ({ companies }) => {
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const [filterText, setFilterText] = useState<string>('');
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch<StoreDispatch>()
    const company: any = useSelector((stateModule: RootState) => stateModule.companyName)!
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const filteredCompanies = useMemo(() => getCompanySearchMatches(companies, filterText), [companies, filterText])

    // Auto-focus the search input when dropdown opens so typing filters immediately
    useEffect(() => {
        if (showDropdown) searchInputRef.current?.focus()
    }, [showDropdown])

    // Sync URL param ↔ Redux on mount and when company changes
    useEffect(() => {
        const urlCompany = searchParams.get('company')
        if (urlCompany && urlCompany !== company.value) {
            dispatch(update(urlCompany.toLowerCase()))
        } else if (company.value && !urlCompany) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev)
                next.set('company', company.value)
                return next
            }, { replace: true })
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Close on outside click
    useEffect(() => {
        if (!showDropdown) return
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowDropdown(false)
                setFilterText('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showDropdown])

    const handleButtonClick = () => {
        if (showDropdown) setFilterText('')
        if (!showDropdown) notifyDropdownOpen('companies-list')
        setShowDropdown(!showDropdown);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterText(event.target.value.toLowerCase());
    };

    const handleOnSelect = (selected: string) => {
        const normalizedCompany = selected.toLowerCase()
        dispatch(update(normalizedCompany))
        setSearchParams({ company: normalizedCompany }, { replace: true })
        setShowDropdown(false)
        setFilterText('')
    }

    return (
        <div className="company-dropdown" ref={dropdownRef}>
            <button onClick={handleButtonClick} className="company-dropdown__btn">
                <span className="company-dropdown__icon">🏢</span>
                <span className={`company-dropdown__value ${!company.value ? 'company-dropdown__value--placeholder' : ''}`}>
                    {company.value ? utilService.capitalizeFirstLetter(company.value) : 'Select company...'}
                </span>
                <svg className="company-dropdown__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            <div className={`company-dropdown__menu ${showDropdown ? 'company-dropdown__menu--open' : ''}`}>
                <input ref={searchInputRef} type="text" placeholder="Search.." className="company-dropdown__search" value={filterText} onChange={handleInputChange} />
                <div className="company-dropdown__list">
                    {filteredCompanies.map(({ company, matchedEntityName }) => {
                        return (<div key={company.id}
                            className='company-dropdown__item'
                            onClick={() => handleOnSelect(company.name)}
                        >
                            {matchedEntityName ? (
                                <span className="company-dropdown__itemLabel">
                                    {utilService.capitalizeFirstLetter(matchedEntityName)} is in <strong>{utilService.capitalizeFirstLetter(company.name)}</strong>
                                </span>
                            ) : (
                                <span className="company-dropdown__itemLabel">
                                    <strong>{utilService.capitalizeFirstLetter(company.name)}</strong>
                                </span>
                            )}
                        </div>)
                    })}
                </div>
            </div>
        </div>
    );
};
